import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useAuth as useClerkAuth } from "@clerk/react";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";

const Ctx = createContext(null);
const KEY = "obraja_token";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const valid = (t) => {
  try { return !!t && JSON.parse(atob(t.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))).exp * 1000 > Date.now(); } catch { return false; }
};

const FullSpinner = () => <div className="flex min-h-screen items-center justify-center"><div className="h-10 w-10 animate-spin rounded-full border-4 border-[#FFC700] border-t-transparent" /></div>;

function ClerkBridge({ children }) {
  const a = useClerkAuth();
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (a.isLoaded) return;
    const t = setTimeout(() => setSlow(true), 15000);
    return () => clearTimeout(t);
  }, [a.isLoaded]);
  if (!a.isLoaded) return slow ? (
    <div className="mx-auto max-w-md px-4 py-24 text-center" data-testid="auth-load-error">
      <h1 className="text-3xl font-black uppercase">O login está demorando</h1>
      <p className="mt-3 text-sm text-slate-400">Verifique sua internet e tente novamente.</p>
      <button data-testid="auth-load-retry" className="btn-hazard mt-6" onClick={() => window.location.reload()}>Tentar novamente</button>
    </div>
  ) : <FullSpinner />;
  const value = { mode: "clerk", isLoaded: a.isLoaded, isSignedIn: !!a.isSignedIn, getToken: a.getToken, signOut: () => a.signOut() };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

function LocalProvider({ children }) {
  const [token, setToken] = useState(() => (valid(localStorage.getItem(KEY)) ? localStorage.getItem(KEY) : null));
  const getToken = useCallback(async () => localStorage.getItem(KEY), []);
  const signIn = useCallback((t) => { localStorage.setItem(KEY, t); setToken(t); }, []);
  const signOut = useCallback(() => { localStorage.removeItem(KEY); setToken(null); }, []);
  return <Ctx.Provider value={{ mode: "local", isLoaded: true, isSignedIn: !!token, getToken, signIn, signOut }}>{children}</Ctx.Provider>;
}

// Uses Emergent-managed login when it is configured on the backend, else the app's own email/password login.
export function AuthProvider({ children }) {
  const [mode, setMode] = useState(null);
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    let stop = false;
    const load = (n = 0) => fetch(`${BACKEND_URL}/api/clerk/config`).then((r) => r.json())
      .then((c) => { if (!stop) { setCfg(c); setMode(c.enabled ? "clerk" : "local"); } })
      .catch(() => !stop && setTimeout(() => load(n + 1), Math.min(1000 * (n + 1), 5000)));
    load();
    return () => { stop = true; };
  }, []);
  if (!mode) return <FullSpinner />;
  if (mode === "clerk") return <ClerkAuthProvider initialConfig={cfg}><ClerkBridge>{children}</ClerkBridge></ClerkAuthProvider>;
  return <LocalProvider>{children}</LocalProvider>;
}

export const useAuth = () => useContext(Ctx);

export function Show({ when, children }) {
  const a = useAuth();
  if (!a.isLoaded) return null;
  return (when === "signed-in") === a.isSignedIn ? children : null;
}
